// Copyright (c) StrangeBee 2024

import React, { useMemo, ReactElement, useState } from 'react';
import { DisplayEmptyData } from '@Atoms/Display/DisplayEmptyData/DisplayEmptyData';
import UserAvatar from '@Atoms/UserAvatar/UserAvatar';
import { useLocalMessage } from '@Helpers/locale';
import { useDrawer } from '@Hooks/useDrawer';
import { CreateSortableListItem } from '@Molecules/CreateSortableListItem/CreateSortableListItem';
import { THButton } from '@Molecules/Inputs/THButton/THButton';
import { THForm } from '@Molecules/THForm/THForm';
import { CustomFieldValueManage } from '@Organisms/CustomField/CustomFieldValueManage/CustomFieldValueManage';
import DisplayTag from '@Organisms/DisplayTag/DisplayTag';
import { TaskManage } from '@Organisms/Task/TaskManage/TaskManage';
import { CaseCreateSharingManage } from './CaseCreateSharingManage';
import { PageTemplateManage } from '@Organisms/PageTemplate/PageTemplateManage/PageTemplateManage';
import { FormInstance, Space, Tabs } from 'antd';
import { computeEventList } from '@Organisms/KnowledgeBase/eventList';
import client from '@Client';
import { handleNotificationError, openMessage } from '@Helpers/notification';
import { CreateListItem } from '@Molecules/CreateListItem/CreateListItem';
import { getAvailableLinks } from '@Helpers/caseSharing';
import {
    InputShare,
    OrganisationOutput,
    CustomFieldType,
    CustomFieldValueInput,
    ProfileOutput,
    TaskOutput,
    PageTemplateInput,
} from 'thehive-sdk';

interface CustomFieldListItemProps {
    name?: string;
    type?: CustomFieldType;
    value?: string | number | boolean;
}

const CustomFieldListItem = ({ type, name, value }: CustomFieldListItemProps): ReactElement => {
    return (
        <span className="cf-list__item">
            <span className="cf-list__item-name">
                {type} - {name}
            </span>
            {value !== undefined ? <DisplayTag tag={value + ''} color={'default'} /> : null}
        </span>
    );
};

interface CaseCreateFormTabsProps {
    onChange: () => void;
    form: FormInstance;
    currentOrg?: OrganisationOutput;
    orgList: OrganisationOutput[];
    orgProfileList: ProfileOutput[];
    canManageTasks?: boolean;
    canManageCustomFields?: boolean;
    canManageSharing?: boolean;
    pageTemplates?: PageTemplateInput[];
}

export const CaseCreateFormTabs = ({
    onChange,
    form,
    currentOrg,
    orgList,
    orgProfileList,
    canManageCustomFields = true,
    canManageSharing = true,
    canManageTasks = true,
    pageTemplates,
}: CaseCreateFormTabsProps): ReactElement => {
    const i = useLocalMessage();

    const [activeTab, setActiveTab] = useState<'tasks' | 'customFields' | 'pageTemplates' | 'sharings'>(
        canManageTasks
            ? 'tasks'
            : canManageCustomFields
            ? 'customFields'
            : pageTemplates
            ? 'pageTemplates'
            : 'sharings',
    );

    const { visible, setVisible } = useDrawer<TaskOutput>({});

    const { visible: pageTemplatesVisible, setVisible: setPageTemplatesVisible } = useDrawer({});

    const eventList = useMemo(
        () =>
            computeEventList({
                controller: client.PageController,
                locale: i,
                onSuccess: (_, message) => {
                    if (message) {
                        openMessage('success', message);
                    }
                },
                onError: handleNotificationError,
            }),
        [i],
    );

    const availableLinks = useMemo(
        () => currentOrg?.links && getAvailableLinks(currentOrg.links, orgList),
        [currentOrg?.links, orgList],
    );

    return (
        <>
            <Tabs
                className="case-create-form__tabs"
                activeKey={activeTab}
                onChange={(activeKey) => setActiveTab(activeKey as any)}
                tabBarExtraContent={
                    <>
                        {activeTab === 'tasks' && (
                            <THButton type="link" onClick={() => setVisible(true)} data-testid="task-manage">
                                {i('task.actions.add')}
                            </THButton>
                        )}

                        {activeTab === 'customFields' && (
                            <CustomFieldValueManage
                                onFinish={(value) => {
                                    form.setFields([
                                        {
                                            name: 'customFields',
                                            value: [...(form.getFieldValue('customFields') || []), value],
                                        },
                                    ]);
                                    onChange();
                                }}
                                triggerElem={
                                    <THButton type="link" data-testid="custom-field-manage">
                                        {i('customField.actions.add')}
                                    </THButton>
                                }
                            />
                        )}

                        {activeTab === 'pageTemplates' && (
                            <THButton
                                onClick={() => setPageTemplatesVisible(true)}
                                type="link"
                                data-testid="page-templates"
                            >
                                {i('page.actions.add')}
                            </THButton>
                        )}

                        {activeTab === 'sharings' && (
                            <THForm.List name="sharingParameters">
                                {(fields) => (
                                    <CaseCreateSharingManage
                                        fields={fields}
                                        orgList={orgList}
                                        orgProfileList={orgProfileList}
                                        form={form}
                                        triggerElem={
                                            <THButton type="link" data-testid="sharings-manage">
                                                {i('case.actions.share.manage')}
                                            </THButton>
                                        }
                                    />
                                )}
                            </THForm.List>
                        )}
                    </>
                }
            >
                {canManageTasks && (
                    <Tabs.TabPane forceRender tab={i('case.create.form.tasks.label')} key="tasks">
                        <THForm.List name="tasks">
                            {(fields, { remove }): ReactElement => (
                                <>
                                    {!fields.length && (
                                        <DisplayEmptyData label={i('task.none')}>
                                            <THButton type="link" onClick={() => setVisible(true)}>
                                                {i('task.actions.add')}
                                            </THButton>
                                        </DisplayEmptyData>
                                    )}

                                    {fields.map((field, index) => {
                                        const task = form.getFieldValue('tasks')?.[field.name] as
                                            | TaskOutput
                                            | undefined;

                                        return (
                                            <CreateSortableListItem
                                                editDrawer={(onFinish, triggerElem) => (
                                                    <TaskManage
                                                        onFinish={(value) => {
                                                            onFinish(value);
                                                            onChange();
                                                            return Promise.resolve(value);
                                                        }}
                                                        triggerElem={triggerElem}
                                                        defaultValues={task}
                                                    />
                                                )}
                                                title={`${task?.group} - ${task?.title}`}
                                                fieldName="tasks"
                                                key={field.key}
                                                field={field}
                                                index={index}
                                                remove={remove}
                                                form={form}
                                            />
                                        );
                                    })}
                                </>
                            )}
                        </THForm.List>
                    </Tabs.TabPane>
                )}

                {canManageCustomFields && (
                    <Tabs.TabPane forceRender tab={i('case.create.form.custom-fields.label')} key="customFields">
                        <THForm.List name="customFields">
                            {(fields, { add, remove }): ReactElement => (
                                <>
                                    {!fields.length && (
                                        <DisplayEmptyData label={i('customField.none')}>
                                            <CustomFieldValueManage
                                                onFinish={(value) => {
                                                    add(value);
                                                    onChange();
                                                }}
                                                triggerElem={
                                                    <THButton type="link">{i('customField.actions.add')}</THButton>
                                                }
                                            />
                                        </DisplayEmptyData>
                                    )}

                                    {fields.map((field, index) => {
                                        const customField = form.getFieldValue('customFields')?.[field.name] as
                                            | CustomFieldValueInput
                                            | undefined;

                                        return (
                                            <CreateSortableListItem
                                                editDrawer={(onFinish, triggerElem) => (
                                                    <CustomFieldValueManage
                                                        onFinish={(value) => {
                                                            onFinish(value);
                                                            onChange();
                                                        }}
                                                        triggerElem={triggerElem}
                                                        defaultValues={customField}
                                                    />
                                                )}
                                                title={
                                                    <CustomFieldListItem
                                                        type={customField?.type}
                                                        name={customField?.name}
                                                        value={customField?.value}
                                                    />
                                                }
                                                fieldName="customFields"
                                                key={field.key}
                                                field={field}
                                                index={index}
                                                remove={remove}
                                                form={form}
                                            />
                                        );
                                    })}
                                </>
                            )}
                        </THForm.List>
                    </Tabs.TabPane>
                )}

                {pageTemplates && (
                    <Tabs.TabPane forceRender tab={i('case.create.form.page-templates.label')} key="pageTemplates">
                        <THForm.List name="pageTemplates">
                            {(fields, { remove }): ReactElement => (
                                <>
                                    {!fields.length && (
                                        <DisplayEmptyData label={i('page-template.none')}>
                                            <THButton
                                                onClick={() => setPageTemplatesVisible(true)}
                                                type="link"
                                                {...{ 'data-testid': 'page-templates' }}
                                            >
                                                {i('page.actions.add')}
                                            </THButton>
                                        </DisplayEmptyData>
                                    )}
                                    {fields.map((field) => {
                                        const pageTemplate = form.getFieldValue('pageTemplates')?.[field.name] as
                                            | PageTemplateInput
                                            | undefined;

                                        return (
                                            <CreateListItem<PageTemplateInput | undefined>
                                                title={`${pageTemplate?.category} - ${pageTemplate?.title}`}
                                                key={field.key}
                                                field={field}
                                                remove={remove}
                                                removeLabel={i('general.remove')}
                                                form={form}
                                            />
                                        );
                                    })}
                                </>
                            )}
                        </THForm.List>
                    </Tabs.TabPane>
                )}

                {availableLinks?.length && canManageSharing && (
                    <Tabs.TabPane forceRender tab={i('case.create.form.sharing.label')} key="sharings">
                        <THForm.Item noStyle shouldUpdate>
                            {({ getFieldValue }) => {
                                const orgShared = (
                                    getFieldValue('sharingParameters') as (InputShare & {
                                        isShared?: boolean;
                                    })[]
                                ).filter(({ isShared }) => isShared);

                                return (
                                    <div className="case-create-form__sharings">
                                        <p>
                                            {i('case.actions.share.helper', {
                                                count: orgShared.length || '0',
                                            })}
                                        </p>
                                        <div className="case-create-form__sharings__org-list">
                                            {orgShared?.map((value) => {
                                                const { organisation, profile } = value;

                                                const org = orgList.find(({ name }) => name === organisation);

                                                return (
                                                    <div
                                                        key={org?.name}
                                                        className="case-create-form__sharings__org-list-item"
                                                    >
                                                        <Space>
                                                            <UserAvatar avatar={org?.avatar} />
                                                            <span>
                                                                {org?.name} ({profile})
                                                            </span>
                                                        </Space>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                );
                            }}
                        </THForm.Item>
                    </Tabs.TabPane>
                )}
            </Tabs>
            {visible && (
                <TaskManage
                    visible={visible}
                    onVisibleChange={setVisible}
                    onFinish={(value) => {
                        form.setFields([
                            {
                                name: 'tasks',
                                value: [...(form.getFieldValue('tasks') || []), value],
                            },
                        ]);
                        onChange();
                        return Promise.resolve(value);
                    }}
                />
            )}
            {pageTemplatesVisible && (
                <PageTemplateManage
                    drawerTitle={i('page.actions.adding')}
                    ctaMode={['create', 'use']}
                    onCreate={eventList.onCreate}
                    onFinish={(value) => {
                        form.setFields([
                            {
                                name: 'pageTemplates',
                                value: [...(form.getFieldValue('pageTemplates') || []), value],
                            },
                        ]);
                        onChange();
                    }}
                    visible={pageTemplatesVisible}
                    setVisible={setPageTemplatesVisible}
                />
            )}
        </>
    );
};
